import { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { DATE_FORMATS } from '@callmart/types';
import { getUpcomingMonths, getDateRangeFromStartDateByAddingMonths } from '../utils/dateUtils';
import { getValueForMonthColumn, createTableRows } from '../utils/agGridUtils';
import { formatNumber } from '../utils/numberUtils';
import { uploadWFMs } from '../services/wfmValue';
import { useAlertMessage, useCustomStyles } from '../hooks';
import UploadXlsxFileButton from '../components/Buttons/UploadXlsxFileButton';
import { parseXLSXData } from '../utils/xlsxUtils';
import PercentageLoadingOverlay from '../components/Loaders/PercentageLoadingOverlay';
import {
    setCommonStateAfterDataUpload,
    showSuccessfulUploadNotification,
    showErrorUploadNotification,
    parseUploadResponse,
} from '../utils/componentUtils';
import HorizontalScrollTable from '../components/UI/HorizontalScrollTable';
import { useCustomerBaseWFMs } from '../hooks/api/wfms';
import { QueryKey } from '../enums/QueryKey';
import { queryClient } from '../services/queryClient';
import Container from '../components/Layout/Container';
import dayjs from '@utils/dayjs';

export const addRowsInMap = ({ wfms, monthColumns }) => {
    const rowsMap = new Map();

    for (const wfm of wfms) {
        const key = `${wfm.id}`;
        const wfmValuesRes = {
            wfm,
        };

        monthColumns.forEach((monthColumn) => {
            const month = dayjs(monthColumn).format(DATE_FORMATS.YEAR_MONTH);

            const wfmValues = wfm.WFMValues.find(
                (wfmValue) => dayjs(new Date(wfmValue.month)).format(DATE_FORMATS.YEAR_MONTH) === month
            );

            const wfmValue = wfmValues !== undefined ? wfmValues.value : null;
            wfmValuesRes[month] = {
                value: wfmValue,
                wfmId: wfm?.id,
            };
        });

        if (rowsMap.has(key)) {
            rowsMap.set(key, {
                ...rowsMap.get(key),
                ...wfmValuesRes,
            });
        } else {
            rowsMap.set(key, {
                ...wfmValuesRes,
            });
        }
    }

    return rowsMap;
};

const CustomerBaseKPIs = ['Customer Base', 'Customer Base Postpaid', 'Customer Base Prepaid', 'Gross Ads'];

const WFM = () => {
    const { t } = useTranslation();
    const classes = useCustomStyles();
    const { dispatchAlert } = useAlertMessage();

    const sideBar = useMemo(
        () => ({
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: t('field.columns'),
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                    toolPanelParams: {
                        suppressRowGroups: true,
                        suppressValues: true,
                        suppressPivots: true,
                        suppressPivotMode: true,
                        suppressColumnFilter: false,
                        suppressColumnSelectAll: false,
                        suppressColumnExpandAll: true,
                        suppressColumnMove: false,
                    },
                },
            ],
        }),
        [t]
    );
    const gridRef = useRef(null);
    const [firstMonth, setFirstMonth] = useState(dayjs());

    const [loading, setLoading] = useState(false);
    const [percentage, setPercentage] = useState(0);

    const { mutateAsync: handleUploadWFMs } = useMutation({
        mutationFn: uploadWFMs,
    });

    const createColumnDefs = (month) => {
        const monthColumns = getUpcomingMonths(month, 6);

        // Shared by the cell and the Set Filter list: AG Grid does not apply a
        // column's valueFormatter to its filter values, so without passing it to
        // filterParams too the filter would list the raw true/false.
        const relevanceFormatter = ({ value }) =>
            value === true ? t('common.yes') : value === false ? t('common.no') : '';

        const columns = [
            {
                field: 'wfm.KPI.name',
                headerName: t('wfm.kpi'),
                minWidth: 80,
            },
            {
                field: 'wfm.channel.name',
                headerName: t('wfm.channels'),
                minWidth: 95,
            },
            {
                field: 'wfm.WFMCluster.worktype.worktype',
                headerName: t('field.worktype'),
                minWidth: 115,
            },
            {
                field: 'wfm.WFMCluster.welt.name',
                headerName: t('wfm.welt'),
            },
            {
                field: 'wfm.WFMCluster.B2X.name',
                headerName: t('wfm.b2x'),
            },
            {
                field: 'wfm.WFMCluster.budgetDirector.name',
                headerName: t('wfm.budgetDirector'),
                minWidth: 110,
            },
            {
                field: 'wfm.WFMCluster.headOf.name',
                headerName: t('wfm.headOf'),
            },
            {
                field: 'wfm.WFMCluster.brand.name',
                headerName: t('field.brand'),
            },
            { field: 'wfm.handled', headerName: t('wfm.handledDetail'), minWidth: 105 },
            {
                field: 'wfm.WFMCluster.worktype.allSignOffCluster.name',
                headerName: t('wfm.allSignOffCluster'),
                hide: true,
            },
            {
                field: 'wfm.WFMCluster.worktype.allSignOffCluster.relevance',
                headerName: t('wfm.relevance'),
                // Keep the raw boolean as the cell value so the data itself stays
                // language-independent; translate for display only.
                valueGetter: (params) => params.data.wfm.WFMCluster.worktype?.allSignOffCluster?.relevance ?? null,
                valueFormatter: relevanceFormatter,
                filterParams: { valueFormatter: relevanceFormatter },
                hide: true,
            },
            {
                field: 'wfm.WFMCluster.worktype.allSignOffCluster.wtm',
                headerName: t('wfm.wtm'),
                hide: true,
            },
            {
                field: 'wfm.WFMCluster.worktype.allSignOffCluster.forecasterName',
                headerName: t('field.forecaster'),
                hide: true,
            },
            {
                field: 'wfm.WFMCluster.worktype.allSignOffCluster.weightedSf',
                headerName: t('wfm.weightedSf'),
                hide: true,
            },
            {
                field: 'wfm.WFMCluster.worktype.allSignOffCluster.service.name',
                headerName: t('wfm.serviceSales'),
                hide: true,
            },
        ];

        for (const monthColumn of monthColumns) {
            columns.push({
                field: `${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`,
                headerName: `${monthColumn.format(DATE_FORMATS.MONTH_SHORT_YEAR)}`,
                valueGetter: (params) => getValueForMonthColumn(params, monthColumn),
                valueFormatter: (params) => {
                    const value = formatNumber(params.value);
                    // "12,5 %" with the space, like formatPercent and every other percent cell.
                    return params?.data.wfm.unit === '%' && value ? `${value} %` : value;
                },
                suppressMovable: true,
            });
        }

        return columns;
    };

    const monthColumns = getUpcomingMonths(firstMonth, 6);
    const columnDefs = firstMonth ? createColumnDefs(firstMonth) : [];
    const [wfms] = useCustomerBaseWFMs(firstMonth, CustomerBaseKPIs);
    const tableRows = wfms?.length > 0 ? createTableRows(addRowsInMap({ wfms, monthColumns }), 'id') : [];

    const uploadFiles = (files, closeDropzoneDialog) => {
        setLoading(true);

        const file = files[0];

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = parseXLSXData(e);

            handleUploadWFMs({ data, setPercentage })
                .then((res) => {
                    const resData = parseUploadResponse(res, 'WFM');
                    const { success, errorMessage } = resData;

                    if (success) {
                        showSuccessfulUploadNotification(dispatchAlert);
                    } else {
                        showErrorUploadNotification({
                            dispatchAlert,
                            errorMessage,
                        });
                    }
                })
                .catch((error) => {
                    Sentry.captureException(error);
                    showErrorUploadNotification({
                        dispatchAlert,
                        errorMessage: error.message,
                    });
                })
                .finally(() => {
                    const { dateFrom, dateTo } = getDateRangeFromStartDateByAddingMonths(firstMonth, 6);
                    queryClient.invalidateQueries([QueryKey.WFMs, { dateFrom, dateTo, kpiNames: CustomerBaseKPIs }]);
                    setCommonStateAfterDataUpload(setLoading, setPercentage, closeDropzoneDialog);
                });
        };
        reader.readAsArrayBuffer(file);
    };

    return (
        <Container>
            <Grid container spacing={1}>
                <Grid item xs={10} lg={11}>
                    <Typography variant="h5">{t('wfm.customerBaseTitle')}</Typography>
                </Grid>
                <Grid item xs={2} md={2} lg={1} sx={classes.btnRightAligned}>
                    <UploadXlsxFileButton
                        buttonName={t('action.upload')}
                        uploadFiles={uploadFiles}
                        dialogTitle={t('wfm.uploadDialogTitle')}
                    ></UploadXlsxFileButton>
                </Grid>
                <HorizontalScrollTable
                    firstMonth={firstMonth}
                    changeFirstMonth={setFirstMonth}
                    ref={gridRef}
                    tableRows={tableRows}
                    defaultColDef={{
                        editable: false,
                        filter: true,
                        sortable: true,
                        enablePivot: true,
                        minWidth: 90,
                    }}
                    tableColumnDefs={columnDefs}
                    showOptionToGoOneMonthBackward={false}
                    showOptionToGoMoreMonthsBackward={true}
                    showOptionToGoOneMonthForward={false}
                    showOptionToGoMoreMonthsForward={true}
                    numOfMonthsToMove={6}
                    sideBar={sideBar}
                ></HorizontalScrollTable>
            </Grid>
            <PercentageLoadingOverlay loading={loading} percentage={percentage}></PercentageLoadingOverlay>
        </Container>
    );
};

export default WFM;
